import { CameraMemento, Entity, PickResult, Plugin, SceneModel, Viewer } from '@xeokit/xeokit-sdk';
import { math, ObjectsMemento } from '@xeokit/xeokit-sdk/dist/xeokit-sdk.es.js';
import { utils } from '@xeokit/xeokit-sdk/src/viewer/scene/utils.js';
import {
  MapAxes,
  imagePosToWorldPos,
  mapAspect,
  mapAxes,
  worldPosToImagePos,
} from './map-projection';
import {
  MetaGraph,
  MetaNode,
  MetaSceneModel,
  ModelMetadata,
  nodesFromMetadata,
  nodesFromMetaScene,
  nodesFromRvtElements,
  readElevation,
} from './meta-graph';
import { Storey } from './Storey';
import { StoreyMap } from './StoreyMap.js';
import {
  PickStoreyMapOptions,
  ShowStoreyObjectsOptions,
  StoreyCameraOptions,
  StoreyMapOptions,
} from './types';

const tempVec3a = math.vec3();
const tempMat4 = math.mat4();

/* Object types excluded from storey minimaps: grids and their axes carry no useful 2D
   footprint and clutter the floor plan. */
const MAP_EXCLUDED_TYPES = new Set(['IfcGrid', 'IfcGridAxis', 'Grid']);

type ResolvableEntity = Entity & { originalSystemId?: string | number };

type SceneModelWithMetadata = SceneModel & {
  aabb: number[];
  metadata?: ModelMetadata;
  objects?: { [id: string]: ResolvableEntity };
};

interface StoreyViewsPluginConfig {
  id?: string;
  fitStoreyMaps?: boolean;
}

/**
 * A Viewer plugin that visualizes IfcBuildingStoreys. Storeys are built from each
 * SceneModel's runtime metadata (SceneModel.metadata) rather than viewer.metaScene,
 * so it works when models are loaded with `loadIntoMetaScene: false`.
 */
class StoreyViewsPlugin extends Plugin {
  private _objectsMemento: ObjectsMemento;
  private _cameraMemento: CameraMemento;

  storeys: { [storeyId: string]: Storey } = {};
  modelStoreys: { [modelId: string]: { [storeyId: string]: Storey } } = {};

  private _modelMetaGraphs: { [modelId: string]: MetaGraph } = {};

  /* Resolves a metaObject id (which equals an Entity's originalSystemId) to the Scene Entity.
     Needed because scene.objects is keyed by Entity.id, which may differ from originalSystemId. */
  private _modelEntities: { [modelId: string]: Map<string, ResolvableEntity> } = {};

  private _fitStoreyMaps: boolean;

  private _onModelLoaded = this.viewer.scene.on('modelLoaded', (modelId: string) => {
    this._registerModelStoreys(modelId);
    this.fire('storeys', this.storeys);
  });

  private _storeysList: Storey[] | null = null;

  private _spatialSortFunc: null | ((storey1: Storey, storey2: Storey) => number) = null;

  constructor(viewer: Viewer, cfg: StoreyViewsPluginConfig) {
    super('StoreyViews', viewer);

    this._objectsMemento = new ObjectsMemento();
    this._cameraMemento = new CameraMemento();

    this._fitStoreyMaps = !!cfg.fitStoreyMaps;
  }

  private _getModelGraph(modelId: string): MetaGraph | undefined {
    let graph = this._modelMetaGraphs[modelId];
    if (graph) return graph;

    const model = this.viewer.scene.models[modelId] as unknown as SceneModelWithMetadata;
    const metaObjects = model?.metadata?.metaObjects;
    const rvtElements = model?.metadata?.Elements;

    let nodes: MetaNode[] | undefined;
    if (Array.isArray(metaObjects) && metaObjects.length) {
      nodes = nodesFromMetadata(metaObjects);
    } else if (Array.isArray(rvtElements) && rvtElements.length) {
      nodes = nodesFromRvtElements(rvtElements);
    } else {
      const metaScene = this.viewer.metaScene as unknown as
        | { metaModels?: { [id: string]: MetaSceneModel } }
        | undefined;
      const metaModel = metaScene?.metaModels?.[modelId];
      if (metaModel?.metaObjects && Object.keys(metaModel.metaObjects).length) {
        nodes = nodesFromMetaScene(metaModel);
      }
    }

    if (!nodes?.length) return undefined;

    graph = new MetaGraph(nodes);
    this._modelMetaGraphs[modelId] = graph;
    return graph;
  }

  hasObject(metaId: string): boolean {
    for (const modelId in this._modelMetaGraphs) {
      const graph = this._modelMetaGraphs[modelId];
      if (graph.nodes.has(metaId) || graph.nodesByObjectKey.has(metaId)) return true;
    }
    return false;
  }

  /* Entities are indexed under both ids they can be referenced by: SceneModel.metadata and RVT
     appearances name them by originalSystemId, while viewer.metaScene names them by Entity.id,
     which XKTLoaderPlugin globalizes to "<modelId>#<originalSystemId>". */
  private _buildEntityResolver(model: SceneModelWithMetadata): Map<string, ResolvableEntity> {
    const map = new Map<string, ResolvableEntity>();
    const objects = model.objects ?? {};
    for (const entityId in objects) {
      const entity = objects[entityId];
      map.set(String(entity.id), entity);
      if (entity.originalSystemId != null) map.set(String(entity.originalSystemId), entity);
    }
    return map;
  }

  _registerModelStoreys(modelId: string) {
    const scene = this.viewer.scene;
    const model = scene.models[modelId] as unknown as SceneModelWithMetadata;
    if (!model) return;

    const graph = this._getModelGraph(modelId);
    if (!graph) return;

    const entityByMetaId = this._buildEntityResolver(model);
    this._modelEntities[modelId] = entityByMetaId;

    for (const storeyId of graph.getStoreyIds()) {
      const node = graph.nodes.get(storeyId)!;

      const entityIds: string[] = [];
      for (const objectKey of graph.getSubtreeObjectKeys(storeyId)) {
        const entity = entityByMetaId.get(objectKey);
        if (entity) entityIds.push(String(entity.id));
      }

      /* scene.getAABB([]) falls back to the whole scene, which would make a geometryless
         storey claim the entire model - and every position inside it. */
      if (!entityIds.length) continue;

      const storeyAABB = scene.getAABB(entityIds);

      const storey = new Storey(
        this,
        model.aabb,
        storeyAABB,
        [...storeyAABB],
        modelId,
        storeyId,
        entityIds.length,
        node.name,
        readElevation(node),
      );

      model.once('destroyed', () => {
        this._deregisterModelStoreys(modelId);
        this.fire('storeys', this.storeys);
      });

      this.storeys[storeyId] = storey;
      this._storeysList = null;

      if (!this.modelStoreys[modelId]) {
        this.modelStoreys[modelId] = {};
      }
      this.modelStoreys[modelId][storeyId] = storey;
    }

    this._clipBoundingBoxes(modelId);
    this._fitModelAABB(modelId);
  }

  /* Image axes of the storey maps, derived from the World's up axis. */
  private _mapAxes(): MapAxes {
    const camera = this.viewer.camera;
    return mapAxes(camera.worldUp, camera.worldForward);
  }

  private _verticalAxis(): number {
    const worldUp = this.viewer.camera.worldUp;
    const xUp = worldUp[0] > worldUp[1] && worldUp[0] > worldUp[2];
    const yUp = !xUp && worldUp[1] > worldUp[0] && worldUp[1] > worldUp[2];
    return xUp ? 0 : yUp ? 1 : 2;
  }

  /* Clamp each storey's vertical extents so adjacent floors don't overlap: a storey's floor is
     its `elevation` when the metadata carries one, otherwise the bottom of its own objects, and
     its ceiling is the floor of the storey above. */
  private _clipBoundingBoxes(modelId: string) {
    const storeysList = this.storeysList.filter((storey) => storey.modelId === modelId);
    if (storeysList.length < 2) return;

    const bbIndex = this._verticalAxis();
    const floorOf = (storey: Storey) => storey.elevation ?? storey.storeyAABB[bbIndex];

    for (let i = 0, len = storeysList.length; i < len; i++) {
      const bb = storeysList[i].storeyAABB;
      bb[bbIndex] = Math.max(bb[bbIndex], floorOf(storeysList[i]));

      /* storeysList is sorted highest first, so the previous entry sits above this one. */
      if (i > 0) bb[bbIndex + 3] = Math.min(bb[bbIndex + 3], floorOf(storeysList[i - 1]));
    }
  }

  /* Frames storey maps on the storeys themselves instead of the raw model extents. A survey
     point, site geometry or a grid placed far from the building would otherwise shrink the
     floor plan into a corner of the image. The union keeps one shared scale across storeys. */
  private _fitModelAABB(modelId: string) {
    const storeys = Object.values(this.modelStoreys[modelId] ?? {});
    if (!storeys.length) return;

    const aabb = [...storeys[0].storeyObjectsAABB];
    for (const storey of storeys) {
      for (let i = 0; i < 3; i++) {
        aabb[i] = Math.min(aabb[i], storey.storeyObjectsAABB[i]);
        aabb[i + 3] = Math.max(aabb[i + 3], storey.storeyObjectsAABB[i + 3]);
      }
    }

    for (const storey of storeys) storey.modelAABB = [...aabb];
  }

  _deregisterModelStoreys(modelId: string) {
    const storeys = this.modelStoreys[modelId];
    if (storeys) {
      for (const storeyObjectId in storeys) {
        delete this.storeys[storeyObjectId];
        this._storeysList = null;
      }
    }
    delete this.modelStoreys[modelId];
    delete this._modelMetaGraphs[modelId];
    delete this._modelEntities[modelId];
  }

  get fitStoreyMaps(): boolean {
    return this._fitStoreyMaps;
  }

  get storeysList(): Storey[] {
    if (!this._storeysList) {
      this._storeysList = Object.values(this.storeys);
      this._storeysList.sort(this._getSpatialSortFunc());
    }
    return this._storeysList;
  }

  /* Sort storeys from highest to lowest on the vertical World axis, preferring the
     `elevation` attribute and falling back to the storey AABB. */
  private _getSpatialSortFunc(): (storey1: Storey, storey2: Storey) => number {
    return (
      this._spatialSortFunc ||
      (this._spatialSortFunc = (storey1: Storey, storey2: Storey) => {
        const idx = this._verticalAxis();

        const elevation1 = storey1.elevation;
        const elevation2 = storey2.elevation;

        if (elevation1 !== undefined && elevation2 !== undefined) {
          if (elevation1 > elevation2) return -1;
          if (elevation1 < elevation2) return 1;
          return 0;
        }

        if (storey1.storeyAABB[idx] > storey2.storeyAABB[idx]) return -1;
        if (storey1.storeyAABB[idx] < storey2.storeyAABB[idx]) return 1;
        return 0;
      })
    );
  }

  gotoStoreyCamera(storeyId: string, options: StoreyCameraOptions = {}) {
    const storey = this.storeys[storeyId];

    if (!storey) {
      if (options.done) {
        options.done();
      }
      return;
    }

    const viewer = this.viewer;
    const scene = viewer.scene;
    const camera = scene.camera;
    const storeyAABB = storey.storeyAABB;

    if (
      storeyAABB[3] < storeyAABB[0] ||
      storeyAABB[4] < storeyAABB[1] ||
      storeyAABB[5] < storeyAABB[2]
    ) {
      // Don't fly to an inverted boundary
      if (options.done) {
        options.done();
      }
      return;
    }
    if (
      storeyAABB[3] === storeyAABB[0] &&
      storeyAABB[4] === storeyAABB[1] &&
      storeyAABB[5] === storeyAABB[2]
    ) {
      // Don't fly to an empty boundary
      if (options.done) {
        options.done();
      }
      return;
    }
    const look2 = math.getAABB3Center(storeyAABB);
    const diag = math.getAABB3Diag(storeyAABB);
    const fitFOV = 45; // fitFOV;
    const sca = Math.abs(diag / Math.tan(fitFOV * math.DEGTORAD));

    const orthoScale2 = diag * 1.3;

    const eye2 = tempVec3a;

    eye2[0] = look2[0] + camera.worldUp[0] * sca;
    eye2[1] = look2[1] + camera.worldUp[1] * sca;
    eye2[2] = look2[2] + camera.worldUp[2] * sca;

    const up2 = camera.worldForward;

    if (options.done) {
      viewer.cameraFlight.flyTo(
        utils.apply(options, {
          eye: eye2,
          look: look2,
          up: up2,
          orthoScale: orthoScale2,
        }),
        options.done,
      );
    } else {
      viewer.cameraFlight.jumpTo(
        utils.apply(options, {
          eye: eye2,
          look: look2,
          up: up2,
          orthoScale: orthoScale2,
        }),
      );

      viewer.camera.ortho.scale = orthoScale2;
    }
  }

  showStoreyObjects(storeyId: string, options: ShowStoreyObjectsOptions = {}) {
    const storey = this.storeys[storeyId];

    if (!storey) {
      this.error('IfcBuildingStorey not found with this ID: ' + storeyId);
      return;
    }

    const viewer = this.viewer;
    const scene = viewer.scene;

    if (options.hideOthers) {
      scene.setObjectsVisible(viewer.scene.visibleObjectIds, false);
    }

    const excludeTypes = options.excludeTypes;

    this.withStoreyObjects(storeyId, (entity, meta) => {
      if (!entity) return;
      if (excludeTypes && meta?.type && excludeTypes.has(meta.type)) return;
      entity.visible = true;
    });
  }

  withStoreyObjects(storeyId: string, callback: (entity?: Entity, meta?: MetaNode) => void) {
    const storey = this.storeys[storeyId];
    if (!storey) return;

    const graph = this._modelMetaGraphs[storey.modelId];
    const entities = this._modelEntities[storey.modelId];
    if (!graph || !entities) return;

    for (const objectKey of graph.getSubtreeObjectKeys(storeyId)) {
      const entity = entities.get(objectKey);
      if (entity) callback(entity, graph.nodesByObjectKey.get(objectKey));
    }
  }

  createStoreyMap(storeyId: string, options: StoreyMapOptions = {}): StoreyMap | null {
    const storey = this.storeys[storeyId];
    if (!storey) {
      this.error('IfcBuildingStorey not found with this ID: ' + storeyId);
      return null;
    }

    const viewer = this.viewer;
    const scene = viewer.scene;
    const format = options.format || 'png';
    const aabb = this._fitStoreyMaps ? storey.storeyAABB : storey.modelAABB;
    const aspect = mapAspect(aabb, this._mapAxes());
    const padding = options.padding || 0;
    const captureSectionPlanes = !!options.captureSectionPlanes;

    let width: number;
    let height: number;

    if (options.width && options.height) {
      width = options.width;
      height = options.height;
    } else if (options.height) {
      height = options.height;
      width = Math.round(height / aspect);
    } else if (options.width) {
      width = options.width;
      height = Math.round(width * aspect);
    } else {
      width = 300;
      height = Math.round(width * aspect);
    }

    const mask = {
      visible: true,
    };

    this._objectsMemento.saveObjects(scene, mask);
    this._cameraMemento.saveCamera(scene);

    this.showStoreyObjects(
      storeyId,
      utils.apply(options, {
        hideOthers: true,
        excludeTypes: MAP_EXCLUDED_TYPES,
      }),
    );

    if (!captureSectionPlanes) this._toggleSectionPlanes(false);

    this._arrangeStoreyMapCamera(storey);

    const src = viewer.getSnapshot({
      width: width,
      height: height,
      format: format,
    });

    this._objectsMemento.restoreObjects(scene, mask);
    this._cameraMemento.restoreCamera(scene);
    if (!captureSectionPlanes) this._toggleSectionPlanes(true);

    return new StoreyMap(storeyId, src, format, width, height, padding);
  }

  _toggleSectionPlanes(visible: boolean) {
    const planes = this.viewer.scene.sectionPlanes;
    for (const key in planes) {
      planes[key].active = visible;
    }
  }

  _arrangeStoreyMapCamera(storey: Storey) {
    const viewer = this.viewer;
    const scene = viewer.scene;
    const camera = scene.camera;
    const aabb = this._fitStoreyMaps ? storey.storeyAABB : storey.modelAABB;
    const look = math.getAABB3Center(aabb);
    const sca = 0.5;
    const eye = tempVec3a;
    eye[0] = look[0] + camera.worldUp[0] * sca;
    eye[1] = look[1] + camera.worldUp[1] * sca;
    eye[2] = look[2] + camera.worldUp[2] * sca;
    const up = camera.worldForward;
    viewer.cameraFlight.jumpTo({ eye: eye, look: look, up: up });

    /* The frustum spans the two axes the map shows, and clips along the vertical one. Which
       axes those are depends on the World's up axis, so they come from the camera vectors. */
    const axes = this._mapAxes();
    const halfSize = (axis: number) => (aabb[axis + 3] - aabb[axis]) / 2;
    const across = halfSize(axes.right.axis);
    const down = halfSize(axes.down.axis);
    const depth = halfSize(axes.vertical);

    viewer.camera.customProjection.matrix = math.orthoMat4c(
      -across,
      across,
      -down,
      down,
      -depth,
      depth,
      tempMat4,
    );
    viewer.camera.projection = 'customProjection';
  }

  pickStoreyMap(
    mapImg: HTMLImageElement,
    storeyMap: StoreyMap,
    imagePos: number[],
    options: PickStoreyMapOptions,
  ): PickResult | null {
    const storeyId = storeyMap.storeyId;
    const storey = this.storeys[storeyId];

    if (!storey) {
      return null;
    }

    const aabb = this._fitStoreyMaps ? storey.storeyAABB : storey.modelAABB;
    const camera = this.viewer.camera;

    const origin = math.vec3(imagePosToWorldPos(aabb, imagePos, mapImg, this._mapAxes()));
    const direction = camera.worldUp.map((n: number) => -n);
    const look = math.addVec3(origin, direction, tempVec3a);
    const matrix = math.lookAtMat4v(origin, look, camera.worldForward, tempMat4);

    const pickResult = this.viewer.scene.pick({
      // Picking with arbitrarily-positioned ray
      pickSurface: options.pickSurface,
      matrix,
    });

    return pickResult;
  }

  storeyMapToWorldPos(storeyMap: StoreyMap, imagePos: number[]) {
    const storeyId = storeyMap.storeyId;
    const storey = this.storeys[storeyId];

    if (!storey) {
      return null;
    }

    const aabb = this._fitStoreyMaps ? storey.storeyAABB : storey.modelAABB;

    return math.vec3(imagePosToWorldPos(aabb, imagePos, storeyMap, this._mapAxes()));
  }

  getStoreyContainingWorldPos(worldPos: number[], modelId?: string): string | null {
    const storeys = modelId ? this.modelStoreys[modelId] : this.storeys;
    for (const storeyId in storeys) {
      const storey = storeys[storeyId];
      if (math.point3AABB3AbsoluteIntersect(storey.storeyAABB, worldPos)) {
        return storeyId;
      }
    }
    return null;
  }

  /* Storey whose vertical span contains the position, ignoring where it sits in plan. */
  getStoreyInVerticalRange(worldPos: number[], modelId?: string): string | null {
    const storeys = modelId ? this.modelStoreys[modelId] : this.storeys;
    const vertical = this._verticalAxis();
    for (const storeyId in storeys) {
      const bb = storeys[storeyId].storeyAABB;
      if (worldPos[vertical] >= bb[vertical] && worldPos[vertical] <= bb[vertical + 3]) {
        return storeyId;
      }
    }
    return null;
  }

  isPositionAboveOrBelowBuilding(worldPos: number[], modelId?: string): string | null {
    const storeys = modelId ? this.modelStoreys[modelId] : this.storeys;
    const ids = Object.keys(storeys);
    if (!ids.length) return null;

    const vertical = this._verticalAxis();
    /* storeysList is sorted highest first; these maps are keyed in registration order, so find
       the extremes by elevation rather than trusting key order. */
    const sorted = ids
      .slice()
      .sort((a, b) => storeys[a].storeyAABB[vertical] - storeys[b].storeyAABB[vertical]);
    const lowest = sorted[0];
    const highest = sorted[sorted.length - 1];

    if (worldPos[vertical] < storeys[lowest].storeyAABB[vertical]) return lowest;
    if (worldPos[vertical] > storeys[highest].storeyAABB[vertical + 3]) return highest;
    return null;
  }

  worldPosToStoreyMap(
    mapImage: HTMLImageElement,
    storeyMap: StoreyMap,
    worldPos: number[],
    imagePos: number[],
  ): boolean {
    const storeyId = storeyMap.storeyId;
    const storey = this.storeys[storeyId];

    if (!storey) {
      this.error('IfcBuildingStorey not found with this ID: ' + storeyId);
      return false;
    }

    const aabb = this._fitStoreyMaps ? storey.storeyAABB : storey.modelAABB;

    return worldPosToImagePos(aabb, worldPos, mapImage, this._mapAxes(), imagePos);
  }

  /* Viewing direction projected onto the map image, along the same axes as worldPosToStoreyMap.
     Looking straight down leaves no direction to show, so the vector stays zero rather than
     normalizing into NaN - which would void the marker's CSS transform and strand it. */
  worldDirToStoreyMap(imageDir: number[]) {
    const camera = this.viewer.camera;
    const eyeLookDir = math.subVec3(camera.look, camera.eye, tempVec3a);
    const axes = this._mapAxes();

    imageDir[0] = eyeLookDir[axes.right.axis] * axes.right.sign;
    imageDir[1] = eyeLookDir[axes.down.axis] * axes.down.sign;

    const length = Math.hypot(imageDir[0], imageDir[1]);
    if (length > 0) {
      imageDir[0] /= length;
      imageDir[1] /= length;
    }
  }

  override destroy() {
    this.viewer.scene.off(this._onModelLoaded);
    super.destroy();
  }
}

export { StoreyViewsPlugin };
